'use client'
import { useState, useEffect } from 'react'
import { Button, Card, Switch, Label } from '@sarkiai/ui'
import { Cookie, Settings, ChevronDown, ChevronUp } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslations } from '@/components/I18nProvider'

export interface CookiePreferences {
  essential: boolean
  analytics: boolean
  marketing: boolean
  timestamp: number
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  essential: true,
  analytics: false,
  marketing: false,
  timestamp: Date.now(),
}

export function CookieConsent() {
  const t = useTranslations('cookies')
  const [isVisible, setIsVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES)

  useEffect(() => {
    const storedPreferences = localStorage.getItem('cookie-preferences')
    if (!storedPreferences) {
      const timer = setTimeout(() => setIsVisible(true), 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  const savePreferences = (prefs: CookiePreferences) => {
    const prefsWithTimestamp = { ...prefs, timestamp: Date.now() }
    localStorage.setItem('cookie-preferences', JSON.stringify(prefsWithTimestamp))
    setIsVisible(false)
    window.dispatchEvent(new CustomEvent('cookiePreferencesChanged', { detail: prefsWithTimestamp }))
  }

  const handleAcceptAll = () => {
    savePreferences({ essential: true, analytics: true, marketing: true, timestamp: Date.now() })
  }

  const handleAcceptEssential = () => {
    savePreferences(DEFAULT_PREFERENCES)
  }

  const handleSavePreferences = () => {
    savePreferences(preferences)
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring' as const, damping: 25, stiffness: 300 }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-[100]"
        >
          <Card className="relative overflow-hidden border-2 border-primary/20 bg-background/95 backdrop-blur-lg supports-[backdrop-filter]:bg-background/60 shadow-2xl" role="dialog" aria-labelledby="cookie-title" aria-describedby="cookie-description">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-primary/50" />

            <div className="p-5 sm:p-6">
              <div className="flex flex-col gap-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center" aria-hidden="true">
                      <Cookie className="h-5 w-5 text-primary" aria-hidden="true" />
                    </div>
                  </div>

                  <div className="flex-1 space-y-3">
                    <h3 id="cookie-title" className="font-semibold text-sm sm:text-base mb-1">
                      {t('consent.title')}
                    </h3>
                    <p id="cookie-description" className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {t('consent.description')}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center gap-2 text-xs text-primary hover:underline"
                  aria-expanded={showDetails}
                  aria-controls="cookie-preferences"
                >
                  <Settings className="h-3 w-3" aria-hidden="true" />
                  {t('consent.customize')}
                  {showDetails ? <ChevronUp className="h-3 w-3" aria-hidden="true" /> : <ChevronDown className="h-3 w-3" aria-hidden="true" />}
                </button>

                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="space-y-3 overflow-hidden"
                      id="cookie-preferences"
                    >
                      <div className="flex items-start justify-between gap-3 p-3 bg-muted/30 rounded-lg">
                        <div className="flex-1">
                          <Label htmlFor="cookie-essential" className="text-xs font-medium">{t('essential')}</Label>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {t('essentialDesc')}
                          </p>
                        </div>
                        <Switch id="cookie-essential" checked disabled className="mt-0.5" aria-describedby="essential-desc" />
                      </div>

                      <div className="flex items-start justify-between gap-3 p-3 bg-muted/30 rounded-lg">
                        <div className="flex-1">
                          <Label htmlFor="cookie-analytics" className="text-xs font-medium">{t('analytics')}</Label>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {t('analyticsDesc')}
                          </p>
                        </div>
                        <Switch
                          id="cookie-analytics"
                          checked={preferences.analytics}
                          onCheckedChange={(checked) =>
                            setPreferences({ ...preferences, analytics: checked })
                          }
                          className="mt-0.5"
                        />
                      </div>

                      <div className="flex items-start justify-between gap-3 p-3 bg-muted/30 rounded-lg">
                        <div className="flex-1">
                          <Label htmlFor="cookie-marketing" className="text-xs font-medium">{t('marketing')}</Label>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {t('marketingDesc')}
                          </p>
                        </div>
                        <Switch
                          id="cookie-marketing"
                          checked={preferences.marketing}
                          onCheckedChange={(checked) =>
                            setPreferences({ ...preferences, marketing: checked })
                          }
                          className="mt-0.5"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex flex-col sm:flex-row gap-2">
                  {showDetails ? (
                    <Button
                      onClick={handleSavePreferences}
                      className="text-xs sm:text-sm h-9 flex-1"
                    >
                      {t('save')}
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={handleAcceptAll}
                        className="text-xs sm:text-sm h-9 flex-1"
                      >
                        {t('consent.acceptAll')}
                      </Button>
                      <Button
                        onClick={handleAcceptEssential}
                        variant="outline"
                        className="text-xs sm:text-sm h-9 flex-1"
                      >
                        {t('consent.essentialOnly')}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export function getCookiePreferences(): CookiePreferences | null {
  if (typeof window === 'undefined') return null
  const stored = localStorage.getItem('cookie-preferences')
  return stored ? JSON.parse(stored) : null
}

export function reopenCookiePreferences() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('cookie-preferences')
    window.location.reload()
  }
}
